import { t } from '@bia-auction/shared-i18n';
import { IDEMPOTENCY_HEADER, errorEnvelopeSchema } from '@bia-auction/shared-schema';
import { API_BASE } from './config';
import { ensureAccess, refreshAccess } from './token-store';

/** A REST error carrying the server's status + envelope message, and the correlation id
 * (M9, ADR 0023) when the server supplied one — surfaced for support in the error boundary. */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly requestId?: string,
    /** The envelope's stable `error` label, so a caller can branch on the *kind* of failure
     *  rather than on its (already localized) message. Mirrors the admin client. */
    public readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Server `error` labels whose copy the client owns. Lazy (`() => t(...)`) so each is resolved
 * in the language active at the moment of the failure, not at module load.
 */
const LOCALIZED_ERRORS: Record<string, (() => string) | undefined> = {
  Turnstile: () => t('common.turnstileFailed'),
  // The confirmed figure went stale (M19) — a different situation from a failed bid, and the
  // server message is English-only.
  StalePrice: () => t('bidder.detail.priceMoved'),
  // The reset link is spent, expired or unknown (M19) — one server message for all three, and
  // it is English-only.
  ResetToken: () => t('reset.invalidToken'),
  AccountPending: () => t('bidder.login.pending'),
  AccountRejected: () => t('bidder.login.rejected'),
};

async function toError(res: Response): Promise<never> {
  let requestId = res.headers.get('x-request-id') ?? undefined;
  // Rate-limit rejections carry a fixed English server message (no per-request locale for an
  // anonymous login/register); localize by status so the bilingual UI shows the user's language.
  if (res.status === 429) throw new ApiError(429, t('common.rateLimited'), requestId, 'RateLimited');
  let message = res.statusText || `HTTP ${res.status}`;
  let code: string | undefined;
  try {
    const parsed = errorEnvelopeSchema.safeParse(await res.json());
    if (parsed.success) {
      code = parsed.data.error;
      // Some failures use a stable `error` label so we can localize them rather than showing the
      // server's English message: Turnstile (its single-use token often expires between challenge
      // and submit) and the approval gate (M15), where "still under review" and "not approved"
      // need different words and neither is a credentials problem. Everything else keeps the
      // server message.
      message = LOCALIZED_ERRORS[parsed.data.error]?.() ?? parsed.data.message;
      if (parsed.data.requestId) requestId = parsed.data.requestId; // correlation id (M9, ADR 0023)
    }
  } catch {
    // non-JSON body — keep the status text
  }
  throw new ApiError(res.status, message, requestId, code);
}

export interface ApiOptions {
  method?: string;
  body?: unknown;
  /** Send the Bearer token (default true); set false for login/refresh. */
  auth?: boolean;
  /** Abort signal (e.g. to cancel a superseded autosave). */
  signal?: AbortSignal;
  /**
   * Idempotency key for a retry-unsafe POST (M19 — bid placement). One key per user gesture,
   * reused for every retry of it, so a resend whose first response was lost replays the
   * original result instead of committing a second bid.
   */
  idempotencyKey?: string;
}

/** A validator (any Zod schema satisfies this structurally) for runtime response parsing. */
export interface ResponseSchema<T> {
  parse: (data: unknown) => T;
}

/**
 * Typed fetch against the Worker (ADR 0019). Attaches the Bearer token, parses the shared
 * error envelope, and on a 401 refreshes once and retries before giving up (then clears the
 * session). A 204 resolves to `undefined`. When a `schema` is passed the JSON body is
 * **runtime-validated** against the shared Zod contract before it reaches components (AGENTS
 * §runtime-validated shared contract); without one the body is returned as `T` (used for acks
 * whose value the UI ignores).
 */
export async function api<T>(
  path: string,
  opts: ApiOptions = {},
  schema?: ResponseSchema<T>,
): Promise<T> {
  const { method = 'GET', body, auth = true, signal, idempotencyKey } = opts;

  const send = (token: string | null): Promise<Response> =>
    fetch(`${API_BASE}${path}`, {
      method,
      headers: {
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
        ...(idempotencyKey ? { [IDEMPOTENCY_HEADER]: idempotencyKey } : {}),
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...(signal ? { signal } : {}),
    });

  let res = await send(auth ? await ensureAccess() : null);
  if (res.status === 401 && auth) {
    // refreshAccess clears the session only on a definitive rejection (401/403) and preserves
    // it on a transient 429/5xx — so don't clear here, just surface the error.
    const fresh = await refreshAccess();
    if (!fresh) return toError(res);
    res = await send(fresh);
  }
  if (!res.ok) return toError(res);
  if (res.status === 204) return undefined as T;
  const json = await res.json();
  return schema ? schema.parse(json) : (json as T);
}
